import { parse, isValid } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

import { FORMAT_OF_DATE } from '@/utils/constants';

/**
 * Convert a date string from user's timezone to UTC format for start of day
 * Converts 'MM/dd/yyyy' to 'yyyy-MM-ddT00:00:00+00:00'
 *
 * @param {string} dateString - Date string in FORMAT_OF_DATE format (MM/dd/yyyy)
 * @returns {string} - UTC date string in format yyyy-MM-ddTHH:mm:ss+00:00 or empty string
 *
 * @example
 * // User in PST timezone selects 11/06/2025
 * convertLocalDateToUtcStart('11/06/2025')
 * // Returns: '2025-11-06T08:00:00+00:00' (midnight PST = 8am UTC)
 */
export const convertLocalDateToUtcStart = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';

  try {
    // Parse the date string (MM/dd/yyyy) and set time to 00:00:00 in user's timezone
    const localDate = parse(dateString, FORMAT_OF_DATE, new Date());

    // Check if the parsed date is valid
    if (!isValid(localDate)) {
      return '';
    }

    localDate.setHours(0, 0, 0, 0);

    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert to UTC
    const utcDate = zonedTimeToUtc(localDate, userTimezone);

    // Format as yyyy-MM-ddTHH:mm:ss+00:00 using UTC time
    const isoString = utcDate.toISOString();
    // Remove milliseconds and replace Z with +00:00
    return isoString.replace(/\.\d{3}Z$/, '+00:00');
  } catch (error) {
    console.error('Error converting date to UTC start:', error);
    return '';
  }
};

/**
 * Convert a date string from user's timezone to UTC format for end of day
 * Converts 'MM/dd/yyyy' to 'yyyy-MM-ddT23:59:59+00:00'
 *
 * @param {string} dateString - Date string in FORMAT_OF_DATE format (MM/dd/yyyy)
 * @returns {string} - UTC date string in format yyyy-MM-ddTHH:mm:ss+00:00 or empty string
 *
 * @example
 * // User in PST timezone selects 11/06/2025
 * convertLocalDateToUtcEnd('11/06/2025')
 * // Returns: '2025-11-07T07:59:59+00:00' (23:59:59 PST = 7:59:59am UTC next day)
 */
export const convertLocalDateToUtcEnd = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';

  try {
    // Parse the date string (MM/dd/yyyy) and set time to 23:59:59 in user's timezone
    const localDate = parse(dateString, FORMAT_OF_DATE, new Date());

    // Check if the parsed date is valid
    if (!isValid(localDate)) {
      return '';
    }

    localDate.setHours(23, 59, 59, 999);

    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert to UTC
    const utcDate = zonedTimeToUtc(localDate, userTimezone);

    // Format as yyyy-MM-ddTHH:mm:ss+00:00 using UTC time
    const isoString = utcDate.toISOString();
    // Remove milliseconds and replace Z with +00:00
    return isoString.replace(/\.\d{3}Z$/, '+00:00');
  } catch (error) {
    console.error('Error converting date to UTC end:', error);
    return '';
  }
};
